import React from "react";
import { ArrowRight, Leaf as LeafIcon, Utensils, Sun, TreePine, Heart, ShieldCheck, GraduationCap, HandHeart, Sprout } from "lucide-react";
import { SectionHeading, PillLink } from "../components/common";
import { GuideMascot } from "../components/GuideMascot";
import { SunBurst, Star, Squiggle, Leaf, WaveTop } from "../components/Decor";
import { BRAND, PHOTOS, GUIDES, PILLARS, PROGRAMS, MEALS } from "../data/content";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FDFBF7]">
        <div aria-hidden className="absolute top-10 left-6 animate-floaty-slow opacity-90"><SunBurst size={80} /></div>
        <div aria-hidden className="absolute top-28 right-10 animate-bob"><Star size={30} color="#4A90E2" /></div>
        <div aria-hidden className="absolute bottom-24 left-1/3 animate-floaty"><Leaf size={44} /></div>

        <div className="mx-auto max-w-7xl px-5 md:px-8 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rise-in">
            <span className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-soft-sm font-body font-600 text-sm text-[#5f8468]">
              🌿 Organic · Rooted in Love · Indigenous Wisdom
            </span>
            <h1 className="mt-5 font-head font-700 tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#1E293B] leading-[1.05]">
              Where Little Souls{" "}
              <span className="relative inline-block text-[#4A90E2]">
                Learn, Grow
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 200 14" preserveAspectRatio="none"><path d="M2 8 C 50 2, 150 2, 198 8" stroke="#FFD166" strokeWidth="6" fill="none" strokeLinecap="round"/></svg>
              </span>{" "}
              & Shine.
            </h1>
            <p className="mt-6 font-body text-lg text-[#475569] max-w-xl leading-relaxed">
              A loving, child-led learning cove rooted in unschooling, holistic care & Indigenous wisdom — where curiosity leads the way and every child is free to be exactly who they are.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <PillLink to="/enroll" variant="yellow" testid="hero-enroll-btn">Enroll Today <ArrowRight size={18} /></PillLink>
              <PillLink to="/philosophy" variant="ghost" testid="hero-learn-btn">Our Philosophy</PillLink>
            </div>
          </div>

          <div className="relative">
            <div className="relative mx-auto w-full max-w-md aspect-square">
              <img
                src={PHOTOS.heroPlay}
                alt="Diverse children playing together outdoors"
                className="w-full h-full object-cover blob-mask shadow-soft"
              />
              <div aria-hidden className="absolute -z-10 inset-0 bg-[#FFD166] blob-mask-2 translate-x-6 translate-y-6" />
            </div>
            {/* Guide mascots peeking */}
            <div className="absolute -left-2 bottom-4 animate-floaty">
              <GuideMascot src={GUIDES.wave} size={110} ring="#4A90E2" />
            </div>
            <div className="absolute -right-1 top-2 animate-bob">
              <GuideMascot src={GUIDES.leaf} size={96} ring="#84A98C" />
            </div>
          </div>
        </div>
        <WaveTop color="#ffffff" />
      </section>

      {/* GUIDE INTRO STRIP */}
      <section className="bg-white py-14">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6 bg-[#eaf2fc] rounded-3xl p-6 md:p-8 shadow-soft-sm">
            <GuideMascot src={GUIDES.point} size={120} ring="#FFD166" />
            <div>
              <p className="font-hand text-3xl text-[#4A90E2]">Hi there! Come explore with us 👋</p>
              <p className="mt-1 font-body text-[#475569] text-base md:text-lg">
                Our little friends will guide you through everything Divinities Cove has to offer — from our five pillars to our nature-filled Fridays. Follow along!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY TEASER */}
      <section className="bg-white py-8">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <img src={PHOTOS.flowers} alt="Child exploring a field of flowers" className="w-full aspect-[4/3] object-cover blob-mask-2 shadow-soft" />
            <div className="absolute -bottom-6 -right-4 animate-floaty">
              <GuideMascot src={GUIDES.book} size={104} ring="#B983FF" />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="Our Philosophy"
              title="Education that honors the child"
              sub="We believe children are naturally curious, capable, and wise. Instead of asking children to conform to a rigid system, we create an environment that honors how they naturally learn, explore, and grow — guided by unschooling, holistic care, and Indigenous knowledge."
            />
            <div className="mt-6"><PillLink to="/philosophy" variant="primary" testid="philosophy-teaser-btn">Discover Our Approach <ArrowRight size={18} /></PillLink></div>
          </div>
        </div>
      </section>

      {/* FIVE PILLARS TEASER */}
      <section className="py-20 bg-[#FDFBF7] mt-16">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <SectionHeading center eyebrow="The Whole Child" title="Our Five Pillars" sub="Everything we do nurtures the whole child through five interconnected pillars." />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={p.key}
                className="rounded-3xl p-6 text-center shadow-soft-sm hover:-translate-y-1.5 transition-transform duration-200 bg-white border border-black/5"
                style={{ marginTop: i % 2 ? 22 : 0 }}
                data-testid={`pillar-mini-${p.key}`}
              >
                <div className="w-14 h-14 mx-auto rounded-2xl grid place-items-center text-3xl" style={{ background: p.tint }}>{p.emoji}</div>
                <h3 className="mt-4 font-head font-600 text-xl" style={{ color: p.color }}>{p.title}</h3>
                <p className="mt-2 font-body text-sm text-[#475569] leading-snug">{p.blurb}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center"><PillLink to="/pillars" variant="sage" testid="pillars-teaser-btn">Explore the Pillars <ArrowRight size={18} /></PillLink></div>
        </div>
      </section>

      {/* PROGRAMS TEASER */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SectionHeading eyebrow="Flexible Care" title="Programs We Offer" sub="Holistic programs that support children and families through every stage of early learning." />
            <PillLink to="/programs" variant="ghost" testid="programs-teaser-btn">See All Programs <ArrowRight size={18} /></PillLink>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROGRAMS.slice(0, 8).map((prog) => (
              <div key={prog.title} className="rounded-3xl p-6 bg-[#FDFBF7] border border-black/5 shadow-soft-sm hover:-translate-y-1 transition-transform duration-200" data-testid="program-mini">
                <h3 className="font-head font-600 text-lg text-[#1E293B]">{prog.title}</h3>
                <p className="mt-2 font-body text-sm text-[#475569] leading-snug">{prog.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRIDAY / DAY TEASER */}
      <section className="relative py-24 bg-[#4A90E2] overflow-hidden">
        <div aria-hidden className="absolute top-8 right-10 animate-floaty-slow opacity-80"><SunBurst size={70} color="#FFD166" /></div>
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={PHOTOS.forest} alt="Children building a fort in the forest" className="w-full aspect-[4/3] object-cover blob-mask shadow-soft" />
            <div className="absolute -top-5 -left-4 animate-bob"><GuideMascot src={GUIDES.leaf} size={100} ring="#FFD166" /></div>
          </div>
          <div className="text-white">
            <span className="font-hand text-3xl text-[#FFD166]">Fridays are for adventure!</span>
            <h2 className="font-head font-700 tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-1">A flexible day, a natural rhythm</h2>
            <p className="mt-4 font-body text-lg text-white/90 leading-relaxed">
              Instead of a rigid clock, we follow a gentle loop schedule. Fridays are our field trip & outdoors day — and because this is unschooling, anyone who'd rather stay in and play is always welcome too.
            </p>
            <div className="mt-8"><PillLink to="/day" variant="yellow" testid="day-teaser-btn">See A Day With Us <ArrowRight size={18} /></PillLink></div>
          </div>
        </div>
      </section>

      {/* MEALS TEASER */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading eyebrow="Nourishing Bodies" title="Organic & thoughtfully sourced" sub="We are proudly organic — our food is sourced from the very best, and our kitchen is fully meat-free. Breakfast, lunch, snacks, and dinner are all served to children, fresh, home-cooked, and made with love." />
            <div className="mt-8 grid grid-cols-2 gap-4">
              {MEALS.map((m) => (
                <div key={m.name} className="flex items-center gap-3 bg-[#eef4ef] rounded-2xl p-4" data-testid={`meal-mini-${m.name.toLowerCase()}`}>
                  <span className="w-10 h-10 rounded-full bg-white grid place-items-center text-[#5f8468]"><Utensils size={18} /></span>
                  <span className="font-head font-600 text-[#1E293B]">{m.name}</span>
                </div>
              ))}
            </div>
            <div className="mt-8"><PillLink to="/meals" variant="sage" testid="meals-teaser-btn">Our Food Philosophy <ArrowRight size={18} /></PillLink></div>
          </div>
          <div className="relative">
            <img src={PHOTOS.meals} alt="Children sharing a meal together" className="w-full aspect-[4/3] object-cover blob-mask-2 shadow-soft" />
            <div className="absolute -bottom-6 -left-4 animate-floaty"><GuideMascot src={GUIDES.wave} size={100} ring="#84A98C" /></div>
          </div>
        </div>
      </section>

      {/* STAFF / TRUST */}
      <section className="py-20 bg-[#FDFBF7]">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <SectionHeading center eyebrow="Caring, qualified hands" title="Our nurturing team" sub="Every child is cared for by warm, thoughtfully chosen educators you can trust." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: "Background Checked", desc: "Every staff member is fully background checked for your family's peace of mind.", tint: "#eaf2fc", color: "#4A90E2" },
              { icon: Sprout, title: "Holistic Care Trained", desc: "Trained and educated in holistic care that nurtures the whole child.", tint: "#eef4ef", color: "#5f8468" },
              { icon: GraduationCap, title: "Childcare Educated", desc: "Educated in early childhood care and child-led, developmentally-rich learning.", tint: "#f4edff", color: "#B983FF" },
              { icon: HandHeart, title: "Healing Ambassadors", desc: "Family holistic healing ambassadors who support your whole family with heart.", tint: "#fdf1e6", color: "#F4A259" },
            ].map((s) => (
              <div key={s.title} className="rounded-3xl p-7 bg-white border border-black/5 shadow-soft-sm hover:-translate-y-1.5 transition-transform duration-200 text-center" data-testid="staff-value">
                <span className="w-14 h-14 mx-auto rounded-2xl grid place-items-center shadow-soft-sm" style={{ background: s.tint, color: s.color }}><s.icon size={26} /></span>
                <h3 className="mt-4 font-head font-600 text-lg text-[#1E293B]">{s.title}</h3>
                <p className="mt-2 font-body text-sm text-[#475569] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROMISE / CTA */}
      <section className="py-8 bg-white">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="relative rounded-[2.5rem] bg-[#FFD166] p-10 md:p-16 text-center overflow-hidden shadow-soft">
            <div aria-hidden className="absolute -top-6 -left-6 opacity-40"><Leaf size={120} color="#84A98C" /></div>
            <div aria-hidden className="absolute -bottom-8 -right-6 opacity-40 rotate-45"><Leaf size={120} color="#4A90E2" /></div>
            <div className="flex justify-center mb-4"><GuideMascot src={GUIDES.point} size={120} ring="#ffffff" /></div>
            <h2 className="font-head font-700 tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#1E293B]">Come grow with us</h2>
            <p className="mt-4 font-body text-lg text-[#1E293B]/80 max-w-2xl mx-auto">
              Every child carries innate wisdom, limitless potential, and a unique purpose. We'd love to welcome your little one to the cove.
            </p>
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <PillLink to="/enroll" variant="primary" testid="cta-enroll-btn">Start Your Inquiry <ArrowRight size={18} /></PillLink>
            </div>
            <p className="mt-6 font-hand text-3xl text-[#4A90E2]">{BRAND.tagline}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
